import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { EpisodeCard } from "@/components/EpisodeCard";
import { Seo } from "@/components/Seo";

const podcastLinks = [
  {
    label: "Apple Podcasts",
    href: "https://podcasts.apple.com/us/podcast/goodbye-moon/id6797948729",
    testid: "podcast-apple-link",
  },
  {
    label: "Spotify",
    href: "https://open.spotify.com/show/0341pcMwo6LKBzl6YIAQM0",
    testid: "podcast-spotify-link",
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@jewishdeathdoula",
    testid: "podcast-youtube-link",
  },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PodcastPage = () => {
  const [playingId, setPlayingId] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [episodesLoading, setEpisodesLoading] = useState(true);

  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        const res = await axios.get(`${API}/podcast/episodes`);
        setEpisodes(res.data.episodes || []);
      } catch (e) {
        setEpisodes([]);
      } finally {
        setEpisodesLoading(false);
      }
    };
    fetchEpisodes();
  }, []);

  return (
    <div className="bg-[#FCF5EB] text-[#02515A] antialiased" data-testid="podcast-page">
      <Seo
        title="Goodbye Moon — a podcast about death, dying, love and legacy"
        description="Goodbye Moon is a podcast about death, dying, love, legacy, and the conversations we often leave unsaid. Listen to every episode on the page."
      />
      <Navbar />
      <main>
        <section className="pt-44 pb-24 md:pb-32" data-testid="podcast-header">
          <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-14 md:gap-20 items-center">
            <Reveal className="md:col-span-4">
              <div className="overflow-hidden">
                <img
                  src="/images/goodbye-moon.png"
                  alt="Goodbye Moon podcast cover art"
                  className="w-full aspect-square object-cover transition-transform duration-1000 ease-out hover:scale-105"
                  data-testid="podcast-cover"
                />
              </div>
            </Reveal>
            <div className="md:col-span-7 md:col-start-6">
              <Reveal>
                <p className="text-xs tracking-[0.35em] uppercase text-[#7D1B56] mb-8">
                  The Podcast
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h1 className="font-serif-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#02515A]">
                  Goodbye <span className="italic text-[#7D1B56]">Moon</span>
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-8 text-base md:text-lg font-light leading-loose text-[#3F6B72] max-w-xl">
                  A podcast about death, dying, love and legacy — and the
                  conversations we often leave unsaid. Most people will talk
                  about almost anything before they talk about death; this show
                  begins there.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4">
                  {podcastLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#02515A] border-b border-[#B4A893]/60 hover:border-[#02515A] pb-1 transition-colors duration-300"
                      data-testid={link.testid}
                    >
                      {link.label}
                      <ArrowUpRight
                        className="w-3.5 h-3.5 text-[#8F8375] group-hover:text-[#02515A] transition-colors duration-300"
                        strokeWidth={1.5}
                      />
                    </a>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="py-24 md:py-36" data-testid="episodes-section">
          <div className="max-w-4xl mx-auto px-6">
            <Reveal>
              <p className="text-xs tracking-[0.35em] uppercase text-[#7D1B56] mb-6 text-center">
                The Episodes
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif-display font-light text-4xl sm:text-5xl leading-tight text-[#02515A] text-center mb-16">
                Listen <span className="italic text-[#7D1B56]">without leaving the page.</span>
              </h2>
            </Reveal>
            {episodesLoading ? (
              <p className="text-center text-sm font-light text-[#8F8375]" data-testid="episodes-loading">
                Gathering the episodes…
              </p>
            ) : episodes.length === 0 ? (
              <p className="text-center text-sm font-light text-[#8F8375]" data-testid="episodes-empty">
                Episodes could not be loaded here — listen on Apple Podcasts,
                Spotify or YouTube above.
              </p>
            ) : (
              <div className="space-y-6">
                {episodes.map((ep, i) => (
                  <Reveal key={ep.id} delay={Math.min(i, 4) * 0.05}>
                    <EpisodeCard
                      episode={ep}
                      index={i}
                      isPlaying={playingId === ep.id}
                      onPlay={() => setPlayingId(ep.id)}
                      onStop={() => setPlayingId(null)}
                    />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="py-32 md:py-44">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <Reveal>
              <p className="font-serif-display italic font-light text-2xl md:text-3xl text-[#3F6B72] leading-relaxed">
                If a conversation here resonated, you are welcome to begin a
                private one of your own.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <a
                href="/#enquiry"
                className="inline-block mt-10 border border-[#02515A] px-10 py-4 text-xs tracking-[0.25em] uppercase hover:bg-[#02515A] hover:text-[#FCF5EB] transition-colors duration-500"
                data-testid="podcast-enquiry-cta"
              >
                Begin a Conversation
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
